import './styles/globals.css'

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'

import { isSignedOut } from './api/client'
import { Toaster } from './components/ui/sonner'
import { TooltipProvider } from './components/ui/tooltip'
import { ME_QUERY_KEY } from './hooks/useMe'
import { router } from './router'

// When a request finds the session gone while we thought the person was signed in, refresh "me" so signed-in pages
// send them to log in. Never for the "me" request itself, which would ask again forever.
const onSessionLost = (error: unknown, queryKey?: readonly unknown[]) => {
  if (!isSignedOut(error) || queryKey?.[0] === ME_QUERY_KEY[0]) return
  if (queryClient.getQueryData(ME_QUERY_KEY)) void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error, query) => onSessionLost(error, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (error) => onSessionLost(error) }),
  defaultOptions: {
    queries: { retry: (failures, error) => !isSignedOut(error) && failures < 2, refetchOnWindowFocus: true },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('index.html has no #root')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <RouterProvider router={router} />
        <Toaster position="bottom-right" />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
)
